/** "/" = Home/Entdecken — fast entry points, recent searches, curated
 * reliable deals. The actual search lives on /search/:vertical and results
 * on /search/:vertical/results (User-Brief 22.09.2026). */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, CarFront, Flame, History, Loader2, X } from "lucide-react";
import { getPublicFeed, getPublicStats } from "../api/publicApi";
import HomePersonal from "../components/home/HomePersonal";
import WebTour, { shouldShowHomeTour } from "../components/home/WebTour";
import PublicDealCard from "../components/public/PublicDealCard";
import useRecentSearches from "../hooks/useRecentSearches";
import { useAuth } from "../auth/AuthContext";
import { trackFunnel } from "../lib/funnel";
import { paramsToQuery } from "../lib/searchQuery";
import { PopularSearches } from "../components/seo/SeoLinkList";
import { popularSeoPages } from "../seo/landing";

export default function PublicDiscoveryPage() {
  const { t, i18n } = useTranslation(["discover", "enums"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dealsVertical, setDealsVertical] = useState("immo");
  const recent = useRecentSearches();
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      if (shouldShowHomeTour(user)) setTourOpen(true);
    }, 1400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.ulid]);

  useEffect(() => {
    trackFunnel("public_discovery_view", { vertical: dealsVertical, authed: !!user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statsQ = useQuery({ queryKey: ["public-stats"], queryFn: getPublicStats, staleTime: 300_000 });

  // Curated home section: RELIABLE deals only (existing quality rules).
  // This never changes the default of a freshly started normal search.
  const dealsQ = useInfiniteQuery({
    queryKey: ["home-top-deals", dealsVertical],
    queryFn: async ({ pageParam }) =>
      getPublicFeed(dealsVertical, { quality: "deals", sort: "recommended", limit: 8, ...(pageParam || {}) }),
    initialPageParam: null,
    getNextPageParam: () => undefined,
    staleTime: 120_000,
  });
  const dealItems = (dealsQ.data?.pages || []).flatMap((p) => p.items || []).slice(0, 8);

  const openCard = (card) => {
    trackFunnel("deal_detail_open", { ulid: card.id, authed: !!user });
    navigate(user ? `/listing/${card.id}` : `/deal/${card.id}`);
  };
  const runRecent = (r) => {
    navigate(`/search/${r.vertical === "auto" ? "auto" : "immo"}/results?${paramsToQuery(r.params || {})}`);
  };

  const stats = statsQ.data;
  return (
    <div className="min-h-dvh bg-zinc-950 text-zinc-100" data-testid="public-discovery-page">
      {!user && (
        <header className="sticky top-0 z-30 border-b border-zinc-800/80 bg-zinc-950/90 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-3 py-3 sm:px-6">
            <Link to="/" className="flex items-center gap-2 font-mono text-sm">
              <span className="inline-block h-2 w-2 -translate-y-0.5 bg-emerald-400" />
              <span className="font-semibold">DealAlert</span>
              <span className="hidden text-xs text-zinc-500 sm:inline">{t("header.tagline")}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="discovery-login-link" className="rounded-lg px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900">
                {t("header.login")}
              </Link>
              <Link
                to="/register"
                data-testid="discovery-register-cta"
                onClick={() => trackFunnel("register_cta", { surface: "discovery_header" })}
                className="rounded-lg bg-emerald-500 px-3.5 py-1.5 text-sm font-semibold text-zinc-950 hover:bg-emerald-400"
              >
                {t("header.register")}
              </Link>
            </div>
          </div>
        </header>
      )}

      <main className="mx-auto max-w-7xl space-y-6 px-3 py-4 sm:px-6 sm:py-6">
        {/* Entry: Autos suchen / Immobilien suchen */}
        <section className="space-y-3">
          <h1 className="text-xl font-bold sm:text-2xl">{t("home.title")}</h1>
          <div className="grid grid-cols-2 gap-3" data-testid="home-search-ctas">
            <button
              type="button"
              data-testid="home-cta-auto"
              onClick={() => navigate("/search/auto")}
              className="group flex flex-col items-start gap-2 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 text-left transition-colors hover:border-emerald-500/60 sm:p-6"
            >
              <CarFront size={22} className="text-emerald-400" aria-hidden />
              <span className="text-sm font-bold sm:text-lg">{t("home.searchCars")}</span>
              <span className="hidden text-xs text-zinc-500 sm:block">
                {stats && t("home.carsCount", { count: (stats.auto_active || 0).toLocaleString("de-AT") })}
              </span>
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-400 group-hover:gap-2">
                {t("home.start")} <ArrowRight size={12} aria-hidden />
              </span>
            </button>
            <button
              type="button"
              data-testid="home-cta-immo"
              onClick={() => navigate("/search/immo")}
              className="group flex flex-col items-start gap-2 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 text-left transition-colors hover:border-emerald-500/60 sm:p-6"
            >
              <Building2 size={22} className="text-emerald-400" aria-hidden />
              <span className="text-sm font-bold sm:text-lg">{t("home.searchRealEstate")}</span>
              <span className="hidden text-xs text-zinc-500 sm:block">
                {stats && t("home.immoCount", { count: (stats.immo_active || 0).toLocaleString("de-AT") })}
              </span>
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-400 group-hover:gap-2">
                {t("home.start")} <ArrowRight size={12} aria-hidden />
              </span>
            </button>
          </div>
        </section>

        {/* Last 4 executed searches — the shortcut */}
        {recent.items.length > 0 && (
          <section className="space-y-2" data-testid="home-recent-searches">
            <h2 className="text-base font-semibold text-zinc-200">{t("home.recentTitle")}</h2>
            <div className="flex flex-wrap gap-2">
              {recent.items.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900/50 pl-3 text-xs text-zinc-300"
                >
                  <button
                    type="button"
                    data-testid={`recent-run-${r.id}`}
                    onClick={() => runRecent(r)}
                    className="inline-flex items-center gap-1.5 py-2 hover:text-emerald-300"
                  >
                    <History size={12} className="text-zinc-500" aria-hidden />
                    {r.label}
                  </button>
                  <button
                    type="button"
                    aria-label={t("home.removeRecent")}
                    data-testid={`recent-remove-${r.id}`}
                    onClick={() => recent.remove(r.id)}
                    className="px-2 py-2 text-zinc-600 hover:text-zinc-300"
                  >
                    <X size={11} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          </section>
        )}

        {user && <HomePersonal onRunRecent={runRecent} onExplore={() => navigate(`/search/${dealsVertical}/results`)} />}

        {/* Curated reliable deals — home section, independent of search default */}
        <section className="space-y-3" data-testid="home-top-deals">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-zinc-200 md:text-lg">{t("home.topDealsTitle")}</h2>
            <div className="flex rounded-lg border border-zinc-800 p-0.5">
              {["immo", "auto"].map((v) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`home-deals-tab-${v}`}
                  onClick={() => setDealsVertical(v)}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-medium ${
                    dealsVertical === v ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {v === "auto" ? t("hero.cars") : t("hero.realEstate")}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                data-testid="discovery-open-heatmap"
                onClick={() => navigate("/heatmap")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:border-amber-500/60 hover:text-amber-300"
              >
                <Flame size={12} className="text-amber-400" aria-hidden /> {t("heat.cta")}
              </button>
              <button
                type="button"
                data-testid="home-deals-all"
                onClick={() => navigate(`/search/${dealsVertical}/results?quality=deals`)}
                className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
              >
                {t("home.allDeals")} <ArrowRight size={12} aria-hidden />
              </button>
            </div>
          </div>
          {dealsQ.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-zinc-500">
              <Loader2 size={16} className="animate-spin" aria-hidden /> {t("feed.loading")}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="discovery-deal-grid">
              {dealItems.map((card, i) => (
                <PublicDealCard key={`${card.id}-${i}`} card={card} index={i} onOpen={openCard} />
              ))}
            </div>
          )}
        </section>

        {/* SEO-Sprint 06.10.2026 — kuratierte Einstiege. Verlinkt die
            öffentlichen Suchseiten in der aktiven Sprache, damit Nutzer
            UND Crawler sie von der Startseite aus erreichen. */}
        <PopularSearches links={popularSeoPages(i18n.language?.slice(0, 2) || "de", 10)} />
      </main>

      {!user && (
        <footer className="border-t border-zinc-800/80">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-5 gap-y-2 px-3 py-6 text-xs text-zinc-500 sm:px-6">
            {stats && (
              <span data-testid="discovery-inventory-stat" className="text-zinc-600">
                {t("feed.inventoryTotal", { count: (stats.listings_active_total || 0).toLocaleString("de-AT") })}
              </span>
            )}
            <Link to="/welcome" className="hover:text-zinc-300">{t("footer.about")}</Link>
            <Link to="/impressum" className="hover:text-zinc-300">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-zinc-300">{t("footer.privacy")}</Link>
            <Link to="/agb" className="hover:text-zinc-300">{t("footer.terms")}</Link>
            <Link to="/support" className="hover:text-zinc-300">{t("footer.support")}</Link>
            <span className="ml-auto">© {new Date().getFullYear()} DealAlert</span>
          </div>
        </footer>
      )}

      {tourOpen && <WebTour onClose={() => setTourOpen(false)} />}
    </div>
  );
}
